import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import StyledInput from "common/SearchField/Search";
import { useGetInsurancePoliciesService } from "subModule/src/services/insurance/useInsurance";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DeleteInsuranceCompany,
  InsurancePolicy,
} from "subModule/src/services/insurance/interface";
import FormDialog from "common/Modal/Modal";
import { InsuranceProfileService } from "subModule/src/core/services";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import InsuranceTabs from "./InsuranceTabs";
// import PolicyIcon from "@mui/icons-material/Policy";
// import ComponentSubheader from "common/ComponentHeaderTwo/ComponentSubheader";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

export default function InsurancePoliciesListing() {
  const navigate = useNavigate();
  const params = useParams();
  const companyId = params?.id;
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [search, setSearch] = useState("");
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [row, setRow] = useState<InsurancePolicy>();
  const [statusModal, setStatusModal] = useState(false);
  const [companyTitle, setCompanyTitle] = useState("");
  const {
    data: tableData,
    isFetching,
    refetch,
  } = searchTerm
    ? useGetInsurancePoliciesService({
        pageNumber: pageNo,
        size: pageSize,
        title: searchTerm,
        accountId: Number(params?.id),
      })
    : useGetInsurancePoliciesService({
        pageNumber: pageNo,
        size: pageSize,
        accountId: Number(params?.id),
      });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    InsuranceProfileService.getInsuranceCompanyByID(+companyId)
      .then((res: any) => {
        if (!active) return;
        const name =
          res?.data?.title || res?.data?.secondaryTitle || res?.title || "";
        if (name) setCompanyTitle(String(name).trim());
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [companyId]);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { labelKey: "businessSetup" },
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      {
        labelKey: "insuranceCompany",
        ...(companyTitle ? { label: companyTitle } : {}),
        href: companyId ? `/insurance-company/${companyId}` : undefined,
      },
      { labelKey: "insurancePolicies" },
    ],
    [companyId, companyTitle],
  );

  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, searchTerm]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) =>
    navigate(
      "/insurance-company/" + params?.id + "/insurance-policy/" + row.id,
    );
  const showRowView = (row: any) =>
    navigate(
      "/insurance-company/" + params?.id + "/insurance-policy/" + row.id,
    );
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
    if (searchTerm) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const handleSearchClick = () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch !== "") {
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_INSURANCEPOLICY_UPDATE")
      ? checkPrivileges("ROLE_INSURANCEPOLICY_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_INSURANCEPOLICY_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={checkPrivileges("ROLE_INSURANCEPOLICY_CREATE")}
        onClick={() => navigate("/insurance-companies")}
        onSubmit={() =>
          navigate(`/insurance-company/${params?.id}/insurance-policy/create`)
        }
        heading={t("insuranceCompanies")}
        icon={
          <AssuredWorkloadOutlinedIcon
            sx={{ fill: "#000", width: "30px", height: "30px" }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
        saveBtnText={"create"}
        isBtnEnabled={true}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className="flex">
        <InsuranceTabs params={params} selectedIndex={1} />
        <Box className="w-[100%] mt-[10px]">
          {/* <ComponentSubheader
            showBtn={true}
            showSaveBtn={false}
            onClick={() =>
              navigate(
                `/insurance-company/${params?.id}/insurance-policy/create`,
              )
            }
            heading={t("insurancePolicies")}
            icon={
              <PolicyIcon
                sx={{ fill: "#000", width: "30px", height: "30px" }}
              />
            }
            btnText={"create"}
            btnType={"create"}
            showCreateBtn={checkPrivileges("ROLE_INSURANCEPOLICY_CREATE")}
          /> */}
          {isFetching || loading ? (
            <Loader />
          ) : (
            <div id="mainListingTable">
              <BackendPaginationTable
                columns={[
                  { id: "title", label: "title", numeric: false },
                  { id: "policyNumber", label: "policyNumber", numeric: false },
                  ...(checkPrivileges("ROLE_INSURANCEPOLICY_ACTIVE")
                    ? [{ id: "active", label: "status", numeric: false }]
                    : []),
                  ...(adjustActions(actionslist).length
                    ? [{ id: "actions", label: "actions", numeric: false }]
                    : []),
                ]}
                data={tableData?.pages[0]?.data?.content || []}
                pageNo={pageNo}
                pageSize={pageSize}
                tableData={tableData?.pages[0]?.data || {}}
                showCheckbox={false}
                showActions={true}
                enableSorting={true}
                showHeader={true}
                showSearch={true}
                showFilter={true}
                showCreateButton={true}
                actions={adjustActions(actionslist)}
                handleChangePage={handleChangePage}
                handleChangeRows={handleChangeRows}
                onChangeStatus={onChangeStatus}
                rowClick={checkPrivileges("ROLE_INSURANCEPOLICY_UPDATE")}
                showRowView={showRowView}
                loadingSearch={false}
                headerComponent={
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7} lg={5}>
                      <StyledInput
                        fullWidth
                        placeholder={t("title")}
                        handleSearchClick={handleSearchClick}
                        handleChange={handleInputChange}
                        value={search}
                        clearSearch={clearSearch}
                      />
                    </Grid>
                  </Grid>
                }
              />
            </div>
          )}
        </Box>
      </Box>
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("INSURANCEPOLICY")}
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setLoading(true);
            setDeleteModal(false);
            InsuranceProfileService.deleteInsurancePolicy((row as any).id)
              .then((response: DeleteInsuranceCompany) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("INSURANCEPOLICY") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            InsuranceProfileService.changeStatusInsurancePolicy(
              (row as any).id,
              row?.active ? "inactive" : "active",
              {},
            )
              .then((response: DeleteInsuranceCompany) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
